import { useCallback, useEffect, useMemo, useState } from "react";
import { apiFetch } from "../../../config/api";
import { formatDate } from "../../../config/utils";
import EmptyState from "../../../components/EmptyState";
import Loader from "../../../components/Loader";
import PageHeader from "../../../components/PageHeader";
import Pagination from "../../../components/Pagination";
import { ClientPortalBadge } from "../clientPortalUtils";

const PAGE_SIZE = 10;
const STATUSES = ["All", "Open", "In Progress", "Waiting Client", "Resolved", "Closed"];
const PRIORITIES = ["Low", "Medium", "High", "Urgent"];
const EMPTY_FORM = {
  clientAccountId: "",
  projectId: "",
  title: "",
  category: "Support",
  priority: "Medium",
  description: "",
  raisedByName: "",
  raisedByEmail: "",
};

export default function ClientRequestsPage() {
  const [requests, setRequests] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("All");
  const [accountId, setAccountId] = useState("");
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const loadLookups = useCallback(async () => {
    try {
      const [accountsRes, projectsRes] = await Promise.all([
        apiFetch("/api/client-portal/accounts"),
        apiFetch("/api/projects"),
      ]);
      if (accountsRes.ok) setAccounts(await accountsRes.json());
      if (projectsRes.ok) setProjects(await projectsRes.json());
    } catch {
      setAccounts([]);
      setProjects([]);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams();
      if (status !== "All") params.set("status", status);
      if (accountId) params.set("accountId", accountId);
      const res = await apiFetch(`/api/client-portal/requests?${params.toString()}`);
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setRequests(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.message || "Could not load client requests.");
    } finally {
      setLoading(false);
    }
  }, [accountId, status]);

  useEffect(() => { loadLookups(); }, [loadLookups]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [accountId, status]);

  const totalPages = Math.max(1, Math.ceil(requests.length / PAGE_SIZE));
  const pagedRequests = useMemo(() => {
    const safePage = Math.min(page, totalPages);
    return requests.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  }, [page, requests, totalPages]);

  function updateField(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  function selectAccount(nextAccountId) {
    const account = accounts.find((item) => item.id === nextAccountId);
    setForm((current) => ({
      ...current,
      clientAccountId: nextAccountId,
      projectId: account?.linkedProjectId || current.projectId,
      raisedByName: account?.primaryContactName || current.raisedByName,
      raisedByEmail: account?.email || current.raisedByEmail,
    }));
  }

  function openCreate() {
    setForm({
      ...EMPTY_FORM,
      clientAccountId: accountId || "",
    });
    setModalOpen(true);
  }

  async function createRequest(event) {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = {
        ...form,
        projectId: form.projectId || null,
      };
      const res = await apiFetch("/api/client-portal/requests", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(await res.text());
      setModalOpen(false);
      await load();
    } catch (err) {
      setError(err?.message || "Could not create request.");
    } finally {
      setSaving(false);
    }
  }

  async function updateStatus(requestId, nextStatus) {
    setSaving(true);
    setError("");
    try {
      const res = await apiFetch(`/api/client-portal/requests/${requestId}/status`, {
        method: "PATCH",
        body: JSON.stringify({ status: nextStatus }),
      });
      if (!res.ok) throw new Error(await res.text());
      await load();
    } catch (err) {
      setError(err?.message || "Could not update request.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="client-portal-page">
      <PageHeader
        title="Requests"
        subtitle={`${requests.length} client request${requests.length === 1 ? "" : "s"} in the current view.`}
        actions={<button className="primary" onClick={openCreate}>New Request</button>}
      />

      <div className="crm-toolbar glass-card" style={{ marginBottom: 16 }}>
        <div className="crm-search">
          <select value={accountId} onChange={(event) => setAccountId(event.target.value)} aria-label="Filter by client">
            <option value="">All clients</option>
            {accounts.map((account) => <option key={account.id} value={account.id}>{account.name}</option>)}
          </select>
        </div>
        <div className="crm-stage-filter" aria-label="Request status">
          {STATUSES.map((item) => (
            <button key={item} type="button" className={status === item ? "active" : ""} onClick={() => setStatus(item)}>
              {item}
            </button>
          ))}
        </div>
      </div>

      {error && <div className="error-banner" style={{ marginBottom: 16 }}>{error}</div>}

      <div className="glass-card client-portal-table-card">
        {loading ? (
          <div style={{ padding: 20 }}>
            {[1, 2, 3, 4, 5].map((item) => <Loader key={item} type="skeleton" height={48} style={{ marginBottom: 10 }} />)}
          </div>
        ) : requests.length === 0 ? (
          <EmptyState
            title="No client requests"
            subtitle="Create a request for support, scope changes, access, reporting, or document updates."
            action={<button className="primary" onClick={openCreate}>New Request</button>}
          />
        ) : (
          <>
            <div className="client-portal-table-wrap">
              <table className="client-portal-table">
                <thead>
                  <tr>
                    <th>Request</th>
                    <th>Client</th>
                    <th>Raised By</th>
                    <th>Priority</th>
                    <th>Status</th>
                    <th>Updated</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {pagedRequests.map((request) => (
                    <tr key={request.id}>
                      <td>
                        <div className="client-portal-row-title">{request.title}</div>
                        <div className="client-portal-row-subtitle">{request.category} - {request.projectName || "No project"}</div>
                      </td>
                      <td>{request.clientName}</td>
                      <td>
                        <div>{request.raisedByName || "-"}</div>
                        <div className="client-portal-row-subtitle">{request.raisedByEmail || ""}</div>
                      </td>
                      <td><ClientPortalBadge status={request.priority} /></td>
                      <td><ClientPortalBadge status={request.status} /></td>
                      <td>{formatDate(request.updatedAt)}</td>
                      <td>
                        <div className="client-portal-action-row">
                          {request.status === "Open" && (
                            <button className="btn-ghost" onClick={() => updateStatus(request.id, "In Progress")} disabled={saving}>Start</button>
                          )}
                          {request.status !== "Resolved" && request.status !== "Closed" && (
                            <button className="btn-success" onClick={() => updateStatus(request.id, "Resolved")} disabled={saving}>Resolve</button>
                          )}
                          {request.status !== "Closed" && (
                            <button className="btn-ghost" onClick={() => updateStatus(request.id, "Closed")} disabled={saving}>Close</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={Math.min(page, totalPages)} totalPages={totalPages} onPageChange={setPage} total={requests.length} pageSize={PAGE_SIZE} />
          </>
        )}
      </div>

      {modalOpen && (
        <div className="modal-overlay" onClick={() => setModalOpen(false)}>
          <form className="glass-card modal crm-modal" onSubmit={createRequest} onClick={(event) => event.stopPropagation()}>
            <div className="modal-header">
              <h3>New Client Request</h3>
              <button type="button" className="modal-close" onClick={() => setModalOpen(false)} aria-label="Close">x</button>
            </div>
            <div className="crm-form-grid">
              <label>
                Client
                <select value={form.clientAccountId} onChange={(event) => selectAccount(event.target.value)} required>
                  <option value="">Select client</option>
                  {accounts.map((account) => <option key={account.id} value={account.id}>{account.name}</option>)}
                </select>
              </label>
              <label>
                Project
                <select value={form.projectId} onChange={(event) => updateField("projectId", event.target.value)}>
                  <option value="">Use client default</option>
                  {projects.map((project) => <option key={project.id} value={project.id}>{project.name}</option>)}
                </select>
              </label>
              <label>
                Title
                <input value={form.title} onChange={(event) => updateField("title", event.target.value)} required />
              </label>
              <label>
                Category
                <input value={form.category} onChange={(event) => updateField("category", event.target.value)} />
              </label>
              <label>
                Priority
                <select value={form.priority} onChange={(event) => updateField("priority", event.target.value)}>
                  {PRIORITIES.map((item) => <option key={item}>{item}</option>)}
                </select>
              </label>
              <label>
                Raised By
                <input value={form.raisedByName} onChange={(event) => updateField("raisedByName", event.target.value)} />
              </label>
              <label>
                Raised By Email
                <input type="email" value={form.raisedByEmail} onChange={(event) => updateField("raisedByEmail", event.target.value)} />
              </label>
              <label className="crm-form-wide">
                Description
                <textarea value={form.description} onChange={(event) => updateField("description", event.target.value)} rows={3} />
              </label>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn-ghost" onClick={() => setModalOpen(false)}>Cancel</button>
              <button type="submit" className="primary" disabled={saving}>{saving ? "Saving..." : "Create Request"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
